import { FC, Fragment } from 'react';
import { Flex, Stack, Text, Button, Tooltip } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  PR_CHART_KEYS,
  PR_AGEING_CHARTS,
  PR_HEALTH_INDEX_CHARTS,
  LOC_VS_PR_CYCLE_TIME_CHARTS,
  REVIEW_RESPONSIVENESS_INDEX_CHARTS,
  DEPLOY_CHART_KEYS,
  ISSUE_CHART_KEYS,
  ISSUE_AGE_CHART_KEYS,
  ISSUE_LIFECYCLE_DIST_CHARTS,
  REQUIREMENT_STABILITY_CHARTS,
  CFR_CHART_KEYS,
  WORK_BREAKDOWN_CHARTS,
} from '../../../../shared/utils';
import { useDetailsPageStore } from '../../../../hooks/useDashboardStore';
import { useMetricSidebarStore } from '../../../../hooks/useMetricSidebarStore';
import setQueryParams from '../../../utils/metricTable.utils';

interface NavigationState {
  filterBy: any;
  currentChartMetaData: any;
  payload: any;
  tableType: 'PR' | 'ISSUE';
  metricHeading: string;
}

interface MetricModalHeaderProps {
  heading: string;
  subHeader: React.ReactNode;
  currentChartMeta: any;
  filterBy: any;
  payload: any;
  startDate?: string;
  endDate?: string;
  hideMoreDetails?: boolean;
  isFetching: boolean;
  downloadCSV: () => void;
  downloadPending: boolean;
  downloadAllowed: boolean;
  pathname: string;
  hasGoals: (pathname: string) => boolean;
}

export const MetricModalHeader: FC<MetricModalHeaderProps> = ({
  heading,
  subHeader,
  currentChartMeta,
  filterBy,
  payload,
  startDate,
  endDate,
  hideMoreDetails,
  isFetching,
  downloadCSV,
  downloadPending,
  downloadAllowed,
  pathname,
  hasGoals,
}) => {
  const navigate = useNavigate();
  const { setSelected, setActive } = useDetailsPageStore();
  const { closeSidebar } = useMetricSidebarStore();

  return (
    <Flex justify="space-between" align="flex-start" gap={4}>
      <Stack spacing={1}>
        <Text
          fontFamily="heading"
          fontSize="lg"
          fontWeight="bold"
          color="text.primary"
        >
          {heading}
        </Text>
        {subHeader}
      </Stack>

      {!isFetching && !hasGoals(pathname) && (
        <Flex gap={2} align="center" mr={6}>
          {[
            ...PR_CHART_KEYS,
            ...DEPLOY_CHART_KEYS,
            ...ISSUE_CHART_KEYS,
            ...CFR_CHART_KEYS,
            ...PR_AGEING_CHARTS,
            ...ISSUE_AGE_CHART_KEYS,
            ...REQUIREMENT_STABILITY_CHARTS,
            ...PR_HEALTH_INDEX_CHARTS,
            ...WORK_BREAKDOWN_CHARTS,
            ...ISSUE_LIFECYCLE_DIST_CHARTS,
            ...LOC_VS_PR_CYCLE_TIME_CHARTS,
          ].includes(currentChartMeta?.chartKey) && (
            <Tooltip
              hasArrow
              label={!downloadAllowed ? 'Upgrade plan to download data' : ''}
              isDisabled={downloadAllowed}
            >
              <Button
                variant="tertiary"
                size="sm"
                borderRadius="lg"
                px={4}
                h="32px"
                minW="120px"
                onClick={downloadCSV}
                isLoading={downloadPending}
                isDisabled={!downloadAllowed}
              >
                Download Data
              </Button>
            </Tooltip>
          )}

          {!hideMoreDetails && (
            <Fragment>
              {[
                ...PR_CHART_KEYS,
                ...PR_AGEING_CHARTS,
                ...PR_HEALTH_INDEX_CHARTS,
                ...LOC_VS_PR_CYCLE_TIME_CHARTS,
                ...REVIEW_RESPONSIVENESS_INDEX_CHARTS,
              ].includes(currentChartMeta.chartKey) &&
                currentChartMeta.chartKey !== 'APPROVED_WITHOUT_COMMENT' && (
                  <Button
                    onClick={() => {
                      const state: NavigationState = {
                        tableType: 'PR',
                        metricHeading: heading,
                        currentChartMetaData: JSON.parse(
                          JSON.stringify(currentChartMeta)
                        ),
                        filterBy: JSON.parse(JSON.stringify(filterBy)),
                        payload: JSON.parse(JSON.stringify(payload)),
                      };

                      setSelected({
                        id: 6,
                        startDate: startDate as string,
                        endDate: endDate as string,
                        duration: filterBy.sprintId ? 'Sprint' : 'Custom',
                        sprintId: filterBy.sprintId || '',
                      });
                      setActive(filterBy.sprintId ? 'Sprint' : 'Custom');
                      navigate(setQueryParams(state));
                      closeSidebar();
                    }}
                    variant="primary"
                    size="sm"
                    borderRadius="lg"
                    px={4}
                    h="32px"
                    minW="120px"
                  >
                    More Details
                  </Button>
                )}

              {[
                ...ISSUE_CHART_KEYS,
                ...ISSUE_AGE_CHART_KEYS,
                ...ISSUE_LIFECYCLE_DIST_CHARTS,
                ...REQUIREMENT_STABILITY_CHARTS,
              ].includes(currentChartMeta.chartKey) && (
                <Button
                  onClick={() => {
                    const state: NavigationState = {
                      tableType: 'ISSUE',
                      metricHeading: heading,
                      currentChartMetaData: JSON.parse(
                        JSON.stringify(currentChartMeta)
                      ),
                      filterBy: JSON.parse(JSON.stringify(filterBy)),
                      payload: JSON.parse(JSON.stringify(payload)),
                    };

                    startDate &&
                      endDate &&
                      setSelected({
                        id: 6,
                        startDate: startDate as string,
                        endDate: endDate as string,
                        duration: filterBy.sprintId ? 'Sprint' : 'Custom',
                        sprintId: filterBy.sprintId || '',
                      });
                    setActive(filterBy.sprintId ? 'Sprint' : 'Custom');
                    navigate(setQueryParams(state));
                    closeSidebar();
                  }}
                  variant="primary"
                  size="sm"
                  borderRadius="lg"
                  px={4}
                  h="32px"
                  minW="120px"
                >
                  More Details
                </Button>
              )}
            </Fragment>
          )}
        </Flex>
      )}
    </Flex>
  );
};
